import React, { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { BANK_CODES, BANK_NAMES, BankName } from '../constants/banks';
import { client } from '../lib/client';

interface ProfileFormProps {
  isEdit: boolean;
  defaultValues?: ProfileFormData;
  isLoading: boolean;
  updateUserProfile: (data: { name: string; bank: BankName; account_number: string }) => void;
}

interface ProfileFormData {
  fullName: string;
  bank: BankName;
  accountNumber: string;
}

const ProfileForm: React.FC<ProfileFormProps> = ({ isLoading, updateUserProfile, defaultValues, isEdit=false }) => {
  const shouldAutoFocus = !isEdit;
  const [isBankVerifying, setIsBankVerifying] = useState(false);
  const isLoadingRef = useRef(false);
  const profileForm = useForm<ProfileFormData>({
    defaultValues: {
      fullName: defaultValues?.fullName || '',
      bank: defaultValues?.bank || null,
      accountNumber: defaultValues?.accountNumber || '',
    }
  });

  const verifyBank = async (bankCode: string, bankNum: string) => {
    const response = await client.post('/auth/verify-bank', {
      bank_code: bankCode,
      bank_num: bankNum
    });
    return response.data;
  };

  function isNameMatch(a: string, b: string) {
    if (!a || !b) return false;
    if (a.length < 2 || b.length < 2) return false;
  
    const shorter = a.length <= b.length ? a : b;
    const longer = a.length > b.length ? a : b;
  
    return longer.includes(shorter);
  }
  
  const handleSubmit = async (data: ProfileFormData) => {
    if (isLoadingRef.current) return;
    
    try {
      setIsBankVerifying(true);
      isLoadingRef.current = true;
      
      const bankCode = BANK_CODES[data.bank];
      const bankNum = data.accountNumber;
      
      const bankHolder = await verifyBank(bankCode, bankNum);
      if (!bankHolder.success) {
        profileForm.setError('fullName', { message: '이름 혹은 계좌정보를 다시 확인해주세요.' });
        return;
      }
      const bankHolderName = bankHolder?.holderName;
      if ([2,3,4].includes(bankHolderName.length)) {
        if (bankHolderName !== data.fullName) {
          profileForm.setError('fullName', { message: '이름 혹은 계좌정보를 다시 확인해주세요.' });
          return;
        }
      } else {
        if (!isNameMatch(bankHolderName, data.fullName)) {
          profileForm.setError('fullName', { message: '이름 혹은 계좌정보를 다시 확인해주세요.' });
          return;
        }
      }
      updateUserProfile({
        name: data.fullName,
        bank: data.bank,
        account_number: data.accountNumber
      });
    } catch {
      profileForm.setError('fullName', { message: '이름 혹은 계좌정보를 다시 확인해주세요.' });
    } finally {
      setIsBankVerifying(false);
      isLoadingRef.current = false;
    }
  };

  return (
    <form onSubmit={profileForm.handleSubmit(handleSubmit)} className="w-full space-y-4">
      <div>
        <label>이름</label>
        <input
          type="text"
          id="name"
          autoFocus={shouldAutoFocus}
          {...profileForm.register('fullName', { 
            required: '이름을 입력해주세요',
            minLength: {
              value: 2,
              message: '2자 이상 입력해주세요'
            }
          })}
          placeholder="홍길동"
          className="w-full"
        />
        {profileForm.formState.errors.fullName && (
          <p className="mt-1 ml-2 text-sm text-red-500">{profileForm.formState.errors.fullName.message}</p>
        )}
        <p className="mt-1 ml-2 text-sm text-gray-500">착오송금 방지를 위해 실명을 입력해주세요.</p>
      </div>
      <div>
        <label>주 사용 은행</label>
        <select
          id="bank"
          {...profileForm.register('bank', { 
            required: '은행을 선택해주세요'
          })}
          className="w-full"
        >
          {BANK_NAMES.map((bank) => (
            <option key={bank} value={bank}>
              {bank}
            </option>
          ))}
        </select>
        {profileForm.formState.errors.bank ? (
          <p className="mt-1 ml-2 text-sm text-red-500">{profileForm.formState.errors.bank.message}</p>
        ) : null}
      </div>

      <div>
        <label>계좌번호</label>
        <input
          type="text"
          inputMode='numeric'
          id="accountNumber"
          {...profileForm.register('accountNumber', { 
            required: '계좌번호를 입력해주세요',
            pattern: {
              value: /^\d+$/,
              message: '숫자만 입력해주세요'
            },
            minLength: {
              value: 10,
              message: '올바른 계좌번호를 입력해주세요'
            },
            maxLength: {
              value: 14,
              message: '올바른 계좌번호를 입력해주세요'
            }
          })}
          placeholder="하이픈(-) 없이 숫자만 입력해주세요."
          className="w-full"
          onInput={(e) => {
            // 숫자만 입력되도록 제한
            const target = e.target as HTMLInputElement;
            target.value = target.value.replace(/[^0-9]/g, '');
          }}
          onKeyDown={(e) => {
            if(e.key === 'Enter') {
              e.preventDefault();
              e.currentTarget.blur();
            }
          }}
        />
        {profileForm.formState.errors.accountNumber ? (
          <p className="mt-1 ml-2 text-sm text-red-500">{profileForm.formState.errors.accountNumber.message}</p>
        ) : (
          <p className="mt-1 ml-2 text-sm text-gray-500">고객님의 개인정보는 안전하게 보관되며,<br/> 그룹 멤버들이 고객님께 송금할 때만 공유돼요.</p>
        )}
      </div>
      <div className='w-full flex items-center justify-between mt-8 gap-2 font-semibold text-lg'>
        <button
          type="submit"
          disabled={isLoading || isBankVerifying}
          className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-xl text-white bg-blue-500 "
        >
          {isLoading ? '처리중...' : isEdit ? '정보 수정' : '가입 완료'}
        </button>

      </div>
    </form>
  );
};

export default ProfileForm; 